/**
 * GeoDetector, fetches visitor geo info from the Edge Function.
 * Resolved results are cached in localStorage for 24 hours. A degraded result
 * (the country could not be resolved, so the strictest regime was applied) is
 * never cached, so the next page view asks again instead of pinning the
 * visitor to a guess for a day.
 */

export interface GeoResult {
  country: string | null;
  region: string | null;
  regulation: "gdpr" | "ccpa" | "lgpd" | "pipeda" | "pdpa" | "popia" | "none";
  requiresConsent: boolean;
  /** Set by geo-detect when the country could not be resolved. */
  degraded?: boolean;
}

const GEO_CACHE_KEY = "pb_geo";
const GEO_CACHE_TTL = 24 * 60 * 60 * 1000; // 24h

export class GeoDetector {
  private apiUrl: string;

  constructor(apiUrl: string) {
    this.apiUrl = apiUrl;
  }

  async detect(): Promise<GeoResult> {
    // Try cache first
    try {
      const cached = localStorage.getItem(GEO_CACHE_KEY);
      if (cached) {
        const { data, expiresAt } = JSON.parse(cached) as { data: GeoResult; expiresAt: number };
        // An entry with no country is a degraded result an older geo-detect
        // returned as "no regulation, no consent needed". Never trust it.
        if (Date.now() < expiresAt && data?.country && !data.degraded) return data;
      }
    } catch {
      // Ignore storage errors
    }

    // Fetch from Edge Function
    try {
      const res = await fetch(`${this.apiUrl}/functions/v1/geo-detect`, {
        signal: AbortSignal.timeout(3000),
      });
      if (!res.ok) return this.defaultResult();
      const data = (await res.json()) as GeoResult;
      if (!data || typeof data !== "object" || typeof data.requiresConsent !== "boolean" || typeof data.regulation !== "string") {
        return this.defaultResult();
      }
      if (data.degraded || !data.country) return data;

      // Cache the resolved result
      try {
        localStorage.setItem(
          GEO_CACHE_KEY,
          JSON.stringify({ data, expiresAt: Date.now() + GEO_CACHE_TTL })
        );
      } catch {
        // Ignore
      }

      return data;
    } catch {
      return this.defaultResult();
    }
  }

  private defaultResult(): GeoResult {
    // Default to GDPR (opt-in) for unknown locations: the safest choice
    return {
      country: null,
      region: null,
      regulation: "gdpr",
      requiresConsent: true,
    };
  }
}
